import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalProps, Row } from 'react-bootstrap';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { createModal } from 'react-modal-promise';
import {
  ButtonCancel,
  FieldInput,
  FieldSelectIsActive,
  FormCol,
  FormFilterCard,
  SelectColumnV8,
  TableCellV8,
  TablePaginatedV8,
  order2Sorting,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaFilter, FaTimes } from 'react-icons/fa';
import { FieldSelectRegion } from '../field-select/fs-region';
import { FieldSelectGroup } from '../field-select/fs-cpgroup';
import { FieldSelectCpWasteCategory } from '../field-select/fs-cpWasteCategory';

// -------------------------------------------------------------------------------
// Modal Multi Select Collectionpoints
// -------------------------------------------------------------------------------

const gqlGet = gql`
  query getCollectionpointsForSelect($limit: Int, $offset: Int, $order: String, $where: String) {
    getCollectionpoints(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        name
        email
        addressLine1
        addressCity
        addressNomos
        isActive
      }
      recordCount
    }
  }
`;

/** Επιλεγμένο σημείο — μόνο όσα χρειάζονται οι καλούντες (π.χ. παραλήπτες ενημέρωσης) */
export type SelectedCollectionpoint = {
  readonly id: number;
  readonly name: string;
  readonly email?: string | null;
};

/**
 * Δύο τρόποι επιλογής: είτε τα τσεκαρισμένα σημεία, είτε όλα όσα ταιριάζουν στο φίλτρο
 * (το πλήθος μπορεί να είναι χιλιάδες, οπότε επιστρέφουμε το where και όχι τις γραμμές).
 */
export type MultiCollectionpointsResult =
  | { readonly mode: 'selected'; readonly rows: ReadonlyArray<SelectedCollectionpoint> }
  | { readonly mode: 'filter'; readonly where: string; readonly count: number };

const PAGE_SIZE = 10;

/** Το where χτίζεται εδώ ώστε να είναι ίδιο για το grid και για το «όσα ταιριάζουν στο φίλτρο» */
function buildWhereFromFilters(values): string {
  let where: any = {};
  if (values) {
    if (values.searchText) {
      const likeObj = { $like: '%' + values.searchText + '%' };
      where = {
        ...where,
        $or: [
          { name: likeObj },
          { email: likeObj },
          { addressCity: likeObj },
          { addressNomos: likeObj },
          { addressLine1: likeObj },
        ],
      };
    }
    if (values.regionId) where = { ...where, regionId: values.regionId };
    if (values.addressNomos) where = { ...where, addressNomos: values.addressNomos };
    if (values.isActive !== undefined && values.isActive !== null && values.isActive !== '') {
      where = { ...where, isActive: values.isActive };
    }
    if (values.wasteCategoryIds) where = { ...where, wasteCategoryIds: values.wasteCategoryIds };
    if (values.groupId) where = { ...where, groupId: values.groupId };
  }
  return JSON.stringify(where);
}

const Filters = ({ onChange, initialValues }) => {
  const { t } = useTranslation();

  const filters = {
    searchText: initialValues?.searchText ?? '',
    regionId: initialValues?.regionId ?? null,
    isActive: initialValues?.isActive ?? true,
    wasteCategoryIds: initialValues?.wasteCategoryIds ?? [],
    groupId: initialValues?.groupId ?? null,
  };

  function handleSubmit(values, { setSubmitting }) {
    setSubmitting(false);
    onChange(values);
  }

  return (
    <FormFilterCard
      title={t('Κριτήρια Αναζήτησης')}
      onSubmit={handleSubmit}
      initialValues={filters}
    >
      <Row>
        <FieldInput as={FormCol} name="searchText" label={t('Αναζήτηση')} type="search" />
        <FieldSelectCpWasteCategory as={FormCol} name="wasteCategoryIds" />
        <FieldSelectGroup as={FormCol} label={t('Κατηγορία Δραστηριότητας')} name="groupId" />
        <FieldSelectRegion as={FormCol} name="regionId" label={t('Περιφέρεια')} />
        <FieldSelectIsActive as={FormCol} name="isActive" label={t('Ενεργό')} />
      </Row>
    </FormFilterCard>
  );
};

type Props = {
  readonly open: boolean;
  readonly close: (result: MultiCollectionpointsResult | false) => void;
  readonly title?: string;
  readonly initFilters?: any;
  readonly appStore: any;
  readonly tableStore?: any;
} & ModalProps;

const ModalSelectCollectionpoints = ({
  open,
  close,
  title = 'Επιλογή Σημείων Συλλογής',
  initFilters = undefined,
  appStore,
  tableStore = undefined,
  size = 'xl',
}: Props) => {
  const { t } = useTranslation();
  const initOrderBy = 'name ASC';

  const [filterValues, setFilterValues] = useState(() => ({
    isActive: true,
    ...(initFilters ?? {}),
  }));
  const [initPageIndex, setInitPageIndex] = useState(0);
  const [queryParams, setQueryParams] = useState(() => ({
    offset: 0,
    limit: PAGE_SIZE,
    order: initOrderBy,
    where: buildWhereFromFilters({ isActive: true, ...(initFilters ?? {}) }),
  }));
  const [selection, setSelection] = useState<ReadonlyArray<SelectedCollectionpoint>>([]);

  const [get, { data, loading }] = useLazyQuery(gqlGet, { fetchPolicy: 'network-only' });

  useEffect(() => {
    get({ variables: { ...queryParams } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: ReadonlyArray<any> = data?.getCollectionpoints?.data ?? [];
  const dataCount: number | undefined = data?.getCollectionpoints?.recordCount;

  const filtersChanged = (values) => {
    setFilterValues(values);
    const newParams = { ...queryParams, offset: 0, where: buildWhereFromFilters(values) };
    setQueryParams(newParams);
    setInitPageIndex(0);
    get({ variables: { ...newParams } });
  };

  const pageChanged = useCallback(
    (pageIndex: number, pageSize: number) => {
      const newParams = { ...queryParams, offset: pageSize * pageIndex, limit: pageSize };
      setInitPageIndex(pageIndex);
      setQueryParams(newParams);
      get({ variables: { ...newParams } });
    },
    [get, queryParams],
  );

  /** Η επιλογή διατηρείται και όταν αλλάξει σελίδα ή φίλτρο */
  const onRowSelectionChange = (rowSelection: Record<string, boolean>) => {
    const pageSelected = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => rows.find((row) => String(row.id) === key))
      .filter(Boolean)
      .map((row: any) => ({ id: row.id, name: row.name, email: row.email }));

    setSelection((prev) => [
      ...prev.filter((item) => !rows.some((row) => row.id === item.id)),
      ...pageSelected,
    ]);
  };

  const unselect = (id: number) => setSelection((prev) => prev.filter((item) => item.id !== id));

  const columns = useMemo(
    () => [
      SelectColumnV8(),
      {
        id: 'name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 30,
      },
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: ({ row }) => (
          <div className="text-start">
            <div>{row.original.addressLine1}</div>
            <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {row.original.addressCity}
              {row.original.addressNomos && <>,&nbsp;{row.original.addressNomos}</>}
            </div>
          </div>
        ),
        size: 30,
      },
      {
        id: 'email',
        header: t('Email'),
        columnName: t('Email'),
        accessorKey: 'email',
        orderByField: 'email',
        cell: TableCellV8,
        size: 25,
      },
    ],
    [t],
  );

  return (
    <Modal size={size} show={open} centered backdrop="static">
      <Modal.Header style={{ backgroundColor: '#699ac0', color: 'white' }}>
        <Modal.Title>{t(title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Filters onChange={filtersChanged} initialValues={filterValues} />
        {selection.length > 0 && (
          <div className="mb-2">
            {selection.map((item) => (
              <Button key={item.id} variant="warning" size="sm" className="me-2 mb-2">
                {item.name} <FaTimes className="pb-1" onClick={() => unselect(item.id)} />
              </Button>
            ))}
          </div>
        )}
        <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
          {data ? (
            <TablePaginatedV8
              id="modal-select-collectionpoints"
              appStore={appStore}
              tableStore={tableStore}
              columns={columns as any}
              data={rows as any}
              dataCount={dataCount}
              onPageChange={pageChanged}
              initPageIndex={initPageIndex}
              initSorting={order2Sorting(initOrderBy)}
              fixedPageSize={PAGE_SIZE}
              getRowId={(row: any) => String(row.id)}
              onRowSelectionChange={onRowSelectionChange}
              rowProps={(row: any) => ({
                className: selection.some((item) => item.id === row.original.id)
                  ? 'table-warning'
                  : '',
              })}
              style={{ minHeight: '340px' }}
            />
          ) : (
            <div
              style={{ height: '340px', background: 'rgba(0, 0, 0, 0.03)', borderRadius: '0.3rem' }}
            />
          )}
        </LoadingOverlay>
        <div className="float-end mt-4 pt-3 mb-0">
          <Button
            className="me-3"
            variant="outline-primary"
            size="lg"
            style={{ paddingTop: '4px', paddingBottom: '4px' }}
            disabled={!dataCount}
            onClick={() =>
              close({ mode: 'filter', where: queryParams.where, count: dataCount ?? 0 })
            }
          >
            <FaFilter /> &nbsp;
            {t('Όσα ταιριάζουν στο φίλτρο')}
            {dataCount != null ? ` (${dataCount})` : ''}
          </Button>
          <Button
            className="me-3"
            variant="primary"
            size="lg"
            style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '7rem' }}
            disabled={selection.length === 0}
            onClick={() => close({ mode: 'selected', rows: selection })}
          >
            <FaCheck /> &nbsp;
            {t('Επιλεγμένα')}
            {selection.length > 0 ? ` (${selection.length})` : ''}
          </Button>
          <ButtonCancel onClick={() => close(false)} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

// -------------------------------------------------------------------------------
const promiseModal = createModal(ModalSelectCollectionpoints);

export const promiseModalSelectMultiCollectionpoints = (props): Promise<any> => {
  return promiseModal({ ...props });
};
// -------------------------------------------------------------------------------
